// src/app/[lang]/components/LazyIframe.tsx
'use client';

import React, { useState, useEffect, useRef } from 'react';

interface LazyIframeProps {
  embedCode: string;
  index: number;
  height?: string;
}

const LazyIframe: React.FC<LazyIframeProps> = ({ 
  embedCode, 
  index, 
  height = '80px' 
}) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect(); // Stop observing once loaded
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before coming into view
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Extract src from iframe and convert to data-src for lazy loading
  const processEmbedCode = (html: string, shouldLoad: boolean) => {
    if (!shouldLoad) return '';
    
    // For any iframe, ensure it loads efficiently
    return html
      .replace('<iframe', '<iframe loading="eager"') // Force eager once we decide to load
      .replace(/frameborder="0"/g, 'frameborder="0" style="border-radius: 8px;"'); // Add some styling
  };

  return (
    <div
      ref={containerRef}
      id={`audio-track-${index + 1}`}
      className="mt-2 mb-4 relative"
      style={{ height }}
    >
      {!shouldLoad ? (
        // Universal skeleton placeholder - works for any embed type
        <div 
          className="h-full bg-gray-100 rounded-lg flex items-center px-4"
          style={{ minHeight: height }}
        >
          <div className="flex items-center space-x-3 w-full animate-pulse">
            {/* Play button icon */}
            <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0 flex items-center justify-center">
              <div className="w-0 h-0 border-l-[8px] border-l-gray-400 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1"></div>
            </div>
            {/* Content bars */}
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-gray-300 rounded w-3/4"></div>
              <div className="h-2 bg-gray-300 rounded w-1/2"></div>
            </div>
            {/* Control buttons */}
            <div className="flex space-x-2">
              <div className="w-6 h-6 bg-gray-300 rounded flex-shrink-0"></div>
              <div className="w-6 h-6 bg-gray-300 rounded flex-shrink-0"></div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="h-full [&>iframe]:h-full [&>iframe]:block [&>iframe]:w-full"
          dangerouslySetInnerHTML={{
            __html: processEmbedCode(embedCode, shouldLoad),
          }}
        />
      )}
    </div>
  );
};

export default LazyIframe;